import type {
  HoldingUnitCapabilities,
  HoldingUnitLifecycleStatus,
  HoldingUnitProfile,
} from '@crv/contracts';

const NONE: HoldingUnitCapabilities = {
  canReceiveIntake: false,
  canReceiveTransfer: false,
  canTransferOut: false,
  canAdjust: false,
  canReady: false,
  canShip: false,
  canRetire: false,
  canBuildProduct: false,
  canHoldLeftover: false,
  canReopen: false,
  canDisassemble: false,
};

export function deriveHoldingUnitCapabilities(
  profile: HoldingUnitProfile,
  lifecycleStatus: HoldingUnitLifecycleStatus = 'open',
): HoldingUnitCapabilities {
  if (
    lifecycleStatus === 'shipped' ||
    lifecycleStatus === 'retired' ||
    lifecycleStatus === 'superseded'
  ) {
    return { ...NONE };
  }

  if (lifecycleStatus === 'ready') {
    return deriveReadyCapabilities(profile);
  }

  return deriveOpenCapabilities(profile);
}

export function deriveHoldingUnitEffectiveCapabilities(
  profile: HoldingUnitProfile,
  lifecycleStatus: HoldingUnitLifecycleStatus = 'open',
  capabilityPolicy?: HoldingUnitCapabilities | null,
): HoldingUnitCapabilities {
  const lifecycleCapabilities = deriveHoldingUnitCapabilities(profile, lifecycleStatus);
  if (!capabilityPolicy) {
    return lifecycleCapabilities;
  }
  return andCapabilities(lifecycleCapabilities, capabilityPolicy);
}

function deriveOpenCapabilities(profile: HoldingUnitProfile): HoldingUnitCapabilities {
  switch (profile.unitType) {
    case 'source':
      return {
        ...NONE,
        canReceiveIntake: true,
        canReceiveTransfer: true,
        canTransferOut: true,
        canAdjust: true,
        canReady: true,
        canRetire: true,
      };
    case 'staging':
      return {
        ...NONE,
        canReceiveTransfer: true,
        canTransferOut: true,
        canAdjust: true,
        canRetire: true,
        canBuildProduct: true,
        canHoldLeftover: true,
      };
    case 'product':
      return {
        ...NONE,
        canReceiveIntake: true,
        canReady: true,
        canRetire: true,
        canDisassemble: true,
      };
    case 'leftover':
      return {
        ...NONE,
        canReceiveTransfer: true,
        canTransferOut: true,
        canAdjust: true,
        canReady: true,
        canRetire: true,
      };
    default:
      return assertNever(profile.unitType);
  }
}

function deriveReadyCapabilities(profile: HoldingUnitProfile): HoldingUnitCapabilities {
  switch (profile.unitType) {
    case 'source':
    case 'leftover':
      return {
        ...NONE,
        canTransferOut: true,
        canShip: true,
        canRetire: true,
        canReopen: true,
      };
    case 'product':
      return {
        ...NONE,
        canShip: true,
        canRetire: true,
        canReopen: true,
        canDisassemble: true,
      };
    case 'staging':
      return { ...NONE };
    default:
      return assertNever(profile.unitType);
  }
}

function andCapabilities(
  left: HoldingUnitCapabilities,
  right: HoldingUnitCapabilities,
): HoldingUnitCapabilities {
  return {
    canReceiveIntake: left.canReceiveIntake && right.canReceiveIntake,
    canReceiveTransfer: left.canReceiveTransfer && right.canReceiveTransfer,
    canTransferOut: left.canTransferOut && right.canTransferOut,
    canAdjust: left.canAdjust && right.canAdjust,
    canReady: left.canReady && right.canReady,
    canShip: left.canShip && right.canShip,
    canRetire: left.canRetire && right.canRetire,
    canBuildProduct: left.canBuildProduct && right.canBuildProduct,
    canHoldLeftover: left.canHoldLeftover && right.canHoldLeftover,
    canReopen: left.canReopen && right.canReopen,
    canDisassemble: left.canDisassemble && right.canDisassemble,
  };
}

function assertNever(value: never): never {
  throw new Error(`Unsupported holding unit type: ${String(value)}`);
}
